import { useEffect, useMemo, useState } from "react";

import { Inbox } from "lucide-react";

import { PageContainer } from "~/components/layout/PageContainer";
import { PageHeader } from "~/components/layout/PageHeader";
import { Card, CardContent } from "~/components/ui/card";
import { EmptyState } from "~/components/ui/empty-state";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "~/components/ui/select";
import {
  PageHeaderSkeleton,
  TableSkeleton,
} from "~/components/ui/skeletons";
import { TablePagination } from "~/components/ui/table-pagination";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "~/components/ui/tabs";
import { useClientApprovals } from "~/hooks/useClientApprovals";
import { usePagination } from "~/hooks/usePagination";
import { ClientApprovalRequest } from "~/types/clientApproval";

import { ApprovalDetailSheet } from "./components/ApprovalDetailSheet";
import { ApprovalsTable } from "./components/ApprovalsTable";
import { ApproveDialog } from "./components/ApproveDialog";
import { RejectDialog } from "./components/RejectDialog";

type StatusTab = "pending" | "approved" | "rejected";

const TAB_LABELS: Record<StatusTab, string> = {
  pending: "Pendientes",
  approved: "Aprobados",
  rejected: "Rechazados",
};

const PAGE_SIZE = 10;

const ClientApprovalsPage = () => {
  const {
    requests,
    isLoading,
    approve,
    reject,
    isApproving,
    isRejecting,
  } = useClientApprovals();

  const [tab, setTab] = useState<StatusTab>("pending");
  const [selected, setSelected] = useState<ClientApprovalRequest | null>(null);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [approveOpen, setApproveOpen] = useState(false);
  const [rejectOpen, setRejectOpen] = useState(false);

  const counts = useMemo(
    () => ({
      pending: requests.filter((r) => r.status === "pending").length,
      approved: requests.filter((r) => r.status === "approved").length,
      rejected: requests.filter((r) => r.status === "rejected").length,
    }),
    [requests]
  );

  const visibleRequests = useMemo(
    () => requests.filter((r) => r.status === tab),
    [requests, tab]
  );

  const { page, setPage, pageCount, pageItems, total } = usePagination(
    visibleRequests,
    PAGE_SIZE
  );

  // Reset to the first page whenever the active tab changes.
  useEffect(() => {
    setPage(1);
  }, [tab, setPage]);

  const openDetail = (request: ClientApprovalRequest) => {
    setSelected(request);
    setSheetOpen(true);
  };

  const handleApprove = async (assignedSeller: string | null) => {
    if (!selected) return;
    await approve({ requestId: selected.id, assignedSeller });
    setApproveOpen(false);
    setSheetOpen(false);
  };

  const handleReject = async (reason: string) => {
    if (!selected) return;
    await reject({ requestId: selected.id, reason });
    setRejectOpen(false);
    setSheetOpen(false);
  };

  if (isLoading) {
    return (
      <PageContainer>
        <PageHeaderSkeleton />
        <TableSkeleton rows={8} columns={8} />
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <PageHeader
        title="Aprobación de Clientes"
        description="Revisa los clientes creados por el agente de IA antes de que sean reales."
      />

      <Tabs value={tab} onValueChange={(v) => setTab(v as StatusTab)}>
        {/* Mobile: dropdown selector */}
        <div className="sm:hidden">
          <Select value={tab} onValueChange={(v) => setTab(v as StatusTab)}>
            <SelectTrigger className="h-9">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {(Object.keys(TAB_LABELS) as StatusTab[]).map((key) => (
                <SelectItem key={key} value={key}>
                  {TAB_LABELS[key]} ({counts[key]})
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Desktop: tab bar */}
        <TabsList className="hidden sm:inline-flex">
          {(Object.keys(TAB_LABELS) as StatusTab[]).map((key) => (
            <TabsTrigger key={key} value={key}>
              {TAB_LABELS[key]} ({counts[key]})
            </TabsTrigger>
          ))}
        </TabsList>

        <TabsContent
          value={tab}
          className="mt-4 animate-in fade-in-0 slide-in-from-bottom-1 duration-300"
        >
          {visibleRequests.length === 0 ? (
            <EmptyState
              icon={Inbox}
              title={`No hay solicitudes ${TAB_LABELS[tab].toLowerCase()}`}
              description={
                tab === "pending"
                  ? "Cuando el agente cree un cliente, aparecerá aquí para su aprobación."
                  : undefined
              }
            />
          ) : (
            <Card>
              <CardContent className="p-0">
                <ApprovalsTable
                  requests={pageItems}
                  onRowClick={openDetail}
                />
              </CardContent>
            </Card>
          )}

          {pageCount > 1 && (
            <TablePagination
              className="mt-4"
              page={page}
              pageCount={pageCount}
              totalItems={total}
              pageSize={PAGE_SIZE}
              onPageChange={setPage}
              itemLabel="solicitudes"
            />
          )}
        </TabsContent>
      </Tabs>

      <ApprovalDetailSheet
        request={selected}
        open={sheetOpen}
        onOpenChange={setSheetOpen}
        onApproveClick={() => setApproveOpen(true)}
        onRejectClick={() => setRejectOpen(true)}
      />

      <ApproveDialog
        request={selected}
        open={approveOpen}
        onOpenChange={setApproveOpen}
        onConfirm={handleApprove}
        isSubmitting={isApproving}
      />

      <RejectDialog
        request={selected}
        open={rejectOpen}
        onOpenChange={setRejectOpen}
        onConfirm={handleReject}
        isSubmitting={isRejecting}
      />
    </PageContainer>
  );
};

export default ClientApprovalsPage;
